import React from "react";
import { FiArrowRight } from "@react-icons/all-files/fi/FiArrowRight";
import {
    Header,
    OutputCheckbox,
    OutputCheckboxSelectAll,
    OutputContainer,
    OutputContent,
    OutputThumbnail,
    OutputTile,
    SelectAll,
    ThumbnailContainer,
} from "./DialogueStyles";
import { IDigitalShareItem } from "../../../../Models/IDigitalShareItem";

interface DialogueOutputsProps {
  onOutputsChanged: (outputs: IDigitalShareItem[]) => void;
  outputs: IDigitalShareItem[];
  outputSections: string[];
  isTrainingRendered: boolean;
  onSetTrainingState?: (isTrainingRendered: boolean) => void;
}

const toggleItem = (
    index: number,
    outputs: IDigitalShareItem[],
    onOutputsChanged: (outputs: IDigitalShareItem[]) => void
): void => {
    const newArray: IDigitalShareItem[] = outputs.splice(0);
    newArray[index].isSelected = !newArray[index].isSelected;

    onOutputsChanged(newArray);
};

const toggleAllItems = (
    outputs: IDigitalShareItem[],
    onOutputsChanged: (outputs: IDigitalShareItem[]) => void
): void => {
    const allChecked: boolean
    = outputs.filter((o) => o.isSelected === true).length === outputs.length;
    const newArray: IDigitalShareItem[] = outputs.splice(0);

    if (allChecked) {
        newArray.forEach((o) => (o.isSelected = false));
    } else {
        newArray.forEach((o) => (o.isSelected = true));
    }

    onOutputsChanged(newArray);
};

/*
  get corresponding section items.
  set their selected status.
  merge them back into outputs.
 */
const toggleAllSectionItems = (
    section: string,
    outputs: IDigitalShareItem[],
    onOutputsChanged: (outputs: IDigitalShareItem[]) => void
): void => {
    const newArray: IDigitalShareItem[] = outputs.splice(0);
    const sectionItems: IDigitalShareItem[] = [];
    const indexes: number[] = [];

    for (let i = 0; i < newArray.length; i++) {
        const output = newArray[i];
        if (output.sectionTitle === section) {
            indexes.push(i);
            sectionItems.push(output);
        }
    }

    const allChecked: boolean
    = sectionItems.filter((o) => o.isSelected === true).length
    === sectionItems.length;

    if (allChecked) {
        sectionItems.forEach((o) => (o.isSelected = false));
    } else {
        sectionItems.forEach((o) => (o.isSelected = true));
    }

    sectionItems.forEach((o, i) => (newArray[indexes[i]] = o));

    onOutputsChanged(newArray);
};

const getSectionChecked = (
    section: string,
    outputs: IDigitalShareItem[]
): boolean => {
    const sectionItems = outputs.filter((o) => o.sectionTitle === section);
    const checked = sectionItems.filter((o) => o.isSelected === true).length;

    return sectionItems.length === checked;
};

export const DialogueOutputs = ({
    onOutputsChanged,
    onSetTrainingState,
    outputs,
    outputSections,
    isTrainingRendered,
}: DialogueOutputsProps) => {
    return (
        <OutputContainer>
            <Header
                style={{ height: 45 }}
                className={!isTrainingRendered ? "no-padding-left" : null}
            >
                <div
                    style={{
                        display: "flex",
                        height: "100%",
                        padding: 10,
                        paddingLeft: 0,
                        alignItems: "center",
                    }}
                >
                    {!isTrainingRendered && (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid #e4e4e4",
                                borderBottom: "none",
                                height: 45,
                                width: 52,
                                marginRight: 16,
                            }}
                        >
                            <FiArrowRight
                                size="18"
                                onClick={() => onSetTrainingState(true)}
                            />
                        </div>
                    )}
                    <strong>Select Sections</strong>
                </div>
                <SelectAll>
                    <OutputCheckboxSelectAll
                        type="checkbox"
                        data-testclass={
                            "digital-share-dialog-output-select-all-selections-checkbox"
                        }
                        onChange={() => toggleAllItems(outputs, onOutputsChanged)}
                        checked={
                            outputs.filter((o) => o.isSelected === true).length
              === outputs.length
                        }
                        style={{ position: "relative", top: "0px" }}
                    />
                    <span>Select All</span>
                </SelectAll>
            </Header>
            {outputSections.map((section, i) => {
                const sectionChecked = getSectionChecked(section, outputs);
                return (
                    <React.Fragment key={`${section}-header-${i}`}>
                        {i !== 0 ? (
                            <Header
                                key={`${section}-header`}
                                style={{
                                    fontSize: "11px",
                                    height: "25px",
                                    fontWeight: "bold",
                                }}
                            >
                                {section}
                                <SelectAll style={{ fontSize: "11px", width: "70px" }}>
                                    <OutputCheckboxSelectAll
                                        type="checkbox"
                                        data-testclass={
                                            "digital-share-dialog-select-all-section-checkbox"
                                        }
                                        onChange={() =>
                                            toggleAllSectionItems(section, outputs, onOutputsChanged)
                                        }
                                        checked={sectionChecked}
                                        style={{ position: "relative", top: "0px" }}
                                    />
                                    <span>Select All</span>
                                </SelectAll>
                            </Header>
                        ) : null}
                        <OutputContent
                            key={`${section}-content`}
                            className={!isTrainingRendered ? "full-width" : null}
                        >
                            {outputs.map((o, i) => {
                                if (o.sectionTitle === section) {
                                    return (
                                        <OutputTile
                                            key={`output-tile-${i}`}
                                            className={!isTrainingRendered ? "full-width" : null}
                                        >
                                            <div style={{ fontSize: "11px", marginBottom: "4px" }}>
                                                {o.itemTitle}
                                            </div>
                                            <ThumbnailContainer
                                                className={o.isSelected ? "selected" : null}
                                            >
                                                <OutputThumbnail
                                                    onClick={() =>
                                                        toggleItem(i, outputs, onOutputsChanged)
                                                    }
                                                    data-testclass={
                                                        "digital-share-dialog-output-thumbnail"
                                                    }
                                                    src={o.imgUrl}
                                                />
                                                <OutputCheckbox
                                                    type="checkbox"
                                                    data-testclass={
                                                        "digital-share-dialog-output-checkbox"
                                                    }
                                                    onChange={() =>
                                                        toggleItem(i, outputs, onOutputsChanged)
                                                    }
                                                    checked={o.isSelected}
                                                />
                                            </ThumbnailContainer>
                                        </OutputTile>
                                    );
                                }
                            })}
                        </OutputContent>
                    </React.Fragment>
                );
            })}
        </OutputContainer>
    );
};
